import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// Types for the Firm Services Activation endpoints
interface FirmServicesStatusResponse {
  firm_id: string;
  is_activated: boolean;
  matters_count: number;
}

interface FirmServicesActivationResponse {
  firm_id: string;
  activated: boolean;
  already_active: boolean;
}

interface BulkSyncPreviewResponse {
  firm_id: string;
  firm_name: string;
  matters_count: number;
  dry_run: boolean;
  exclude_statuses: string[];
}

interface BulkSyncExecuteResponse {
  workflow_id: string;
  run_id: string;
  firm_id: string;
  firm_name: string;
  matters_count: number;
  status: string;
}

interface FirmAutoCcEmailsResponse {
  auto_cc_emails: string[];
}

export const firmServicesActivationApi = createApi({
  reducerPath: 'firmServicesActivationApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['FirmServicesStatus'],
  endpoints: (builder) => ({
    getFirmServicesStatus: builder.query<
      FirmServicesStatusResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => `api/firms/${firmId}/services/status/`,
      providesTags: (result, error, { firmId }) => [
        { type: 'FirmServicesStatus', id: firmId },
      ],
    }),
    activateFirmServices: builder.mutation<
      FirmServicesActivationResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => ({
        url: `api/firms/${firmId}/services/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { firmId }) => [
        { type: 'FirmServicesStatus', id: firmId },
      ],
    }),
    previewFirmServicesSync: builder.query<
      BulkSyncPreviewResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => `api/firms/${firmId}/services/sync/preview/`,
    }),
    executeFirmServicesSync: builder.mutation<
      BulkSyncExecuteResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => ({
        url: `api/firms/${firmId}/services/sync/`,
        method: 'POST',
      }),
    }),
    getFirmAutoCcEmails: builder.query<
      FirmAutoCcEmailsResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => `api/firms/${firmId}/services/auto-cc-emails/`,
    }),
  }),
});

export const {
  useGetFirmServicesStatusQuery,
  useActivateFirmServicesMutation,
  useLazyPreviewFirmServicesSyncQuery,
  useExecuteFirmServicesSyncMutation,
  useGetFirmAutoCcEmailsQuery,
} = firmServicesActivationApi;
